import Dexie, { type Table } from 'dexie';

export type AuditEventType =
  | 'profile.change'
  | 'serial.connect'
  | 'serial.disconnect'
  | 'trip.trigger'
  | 'trip.reset'
  | 'export.csv'
  | 'export.pdf'
  | 'modbus.write'
  | 'modbus.read'
  | 'config.change'
  | 'app.start'
  | 'app.stop'
  | 'auth.login'
  | 'auth.login.failed'
  | 'auth.signup'
  | 'auth.logout'
  | 'auth.password.reset'
  | 'auth.password.updated'
  | 'catalog.sync'
  | 'error';

export interface AuditEntry {
  id?: number;
  timestamp: Date;
  type: AuditEventType;
  detail: string;
  severity: 'info' | 'warning' | 'error';
}

class AuditDatabase extends Dexie {
  audit!: Table<AuditEntry>;

  constructor() {
    super('PREVDRIVEAuditDB');
    this.version(1).stores({
      audit: '++id, timestamp, type, severity',
    });
  }
}

export class AuditLogger {
  private db: AuditDatabase | null = null;
  private memory: AuditEntry[] = [];
  private maxEntries = 10000;
  private useMemory = false;

  constructor() {
    if (typeof indexedDB !== 'undefined') {
      try {
        this.db = new AuditDatabase();
      } catch {
        this.useMemory = true;
      }
    } else {
      this.useMemory = true;
    }
  }

  async log(
    type: AuditEventType,
    detail: string,
    severity: AuditEntry['severity'] = 'info',
  ): Promise<void> {
    const entry: AuditEntry = {
      timestamp: new Date(),
      type,
      detail,
      severity,
    };

    if (this.useMemory) {
      this.memory.unshift(entry);
      if (this.memory.length > this.maxEntries) {
        this.memory.pop();
      }
      return;
    }

    try {
      await this.db!.audit.add(entry);
      const count = await this.db!.audit.count();
      if (count > this.maxEntries) {
        const oldest = await this.db!.audit
          .orderBy('id')
          .limit(count - this.maxEntries)
          .toArray();
        if (oldest.length > 0) {
          await this.db!.audit.bulkDelete(oldest.map((e) => e.id!));
        }
      }
    } catch {
      this.useMemory = true;
      await this.log(type, detail, severity);
    }
  }

  async query(options?: {
    type?: AuditEventType;
    severity?: AuditEntry['severity'];
    limit?: number;
    since?: Date;
  }): Promise<AuditEntry[]> {
    if (this.useMemory) {
      return this.queryMemory(options);
    }

    try {
      let collection = this.db!.audit.orderBy('timestamp').reverse();

      if (options?.since) {
        collection = collection.filter((e) => e.timestamp >= options.since!) as typeof collection;
      }

      let results = await collection.toArray();

      if (options?.type) {
        results = results.filter((e) => e.type === options.type);
      }
      if (options?.severity) {
        results = results.filter((e) => e.severity === options.severity);
      }

      return results.slice(0, options?.limit ?? 100);
    } catch {
      this.useMemory = true;
      return this.queryMemory(options);
    }
  }

  async getRecent(limit = 50): Promise<AuditEntry[]> {
    return this.query({ limit });
  }

  async clear(): Promise<void> {
    if (this.useMemory) {
      this.memory = [];
      return;
    }

    try {
      await this.db!.audit.clear();
    } catch {
      this.memory = [];
      this.useMemory = true;
    }
  }

  private queryMemory(options?: {
    type?: AuditEventType;
    severity?: AuditEntry['severity'];
    limit?: number;
    since?: Date;
  }): AuditEntry[] {
    let results = [...this.memory];

    if (options?.since) {
      results = results.filter((e) => e.timestamp >= options.since!);
    }
    if (options?.type) {
      results = results.filter((e) => e.type === options.type);
    }
    if (options?.severity) {
      results = results.filter((e) => e.severity === options.severity);
    }

    return results.slice(0, options?.limit ?? 100);
  }
}
